/**
 * The Inspect tool's readout box (`combatApp/useInspectTool.ts`): a DOM box over the canvas, beside
 * the inspected cell and never over it, like the Measure ruler's chip beside its end. It sits right
 * of the cell, level with its top, and flips to the left when the stage has no room on the right.
 * The body is the host's (`InspectReadout.tsx`); this frame only places it.
 *
 * Placement follows the cell, not the pointer: it moves when the cell or the view changes, so the
 * box never re-renders per pixel of pointer travel. A dragged Boost's drop card
 * (`BoostDropCard`) borrows the same placement through `frame`.
 */
import { useLayoutEffect, useRef, type ReactNode } from "react";
import type { SquareCell } from "@lancer-vtt/domain";
import { CELL } from "../combatCells";

/** Screen px between the cell's edge and the box, and between the box and the stage's edge. */
export const INSPECT_GAP_PX = 10;

/**
 * Where the box goes (stage px): right of the cell when it fits, else left of it, else against the
 * stage's right edge; its top level with the cell's, clamped inside the stage.
 */
export function inspectPlacement(
  cell: { left: number; top: number; size: number },
  box: { width: number; height: number },
  stage: { width: number; height: number },
): { left: number; top: number } {
  const right = cell.left + cell.size + INSPECT_GAP_PX;
  const left = cell.left - INSPECT_GAP_PX - box.width;
  const x = right + box.width + INSPECT_GAP_PX <= stage.width ? right : left >= INSPECT_GAP_PX ? left : Math.max(INSPECT_GAP_PX, stage.width - box.width - INSPECT_GAP_PX);
  const y = Math.max(INSPECT_GAP_PX, Math.min(cell.top, stage.height - box.height - INSPECT_GAP_PX));
  return { left: Math.round(x), top: Math.round(y) };
}

export function InspectReadoutFrame({
  cell,
  view,
  stage,
  children,
  frame = { className: "combat-inspect-readout", role: "status", label: "inspected cell" },
}: {
  cell: SquareCell;
  view: { x: number; y: number; scale: number };
  stage: { width: number; height: number };
  children: ReactNode;
  /** The box's class and accessible role/name; the Inspect readout's by default. */
  frame?: { className: string; role: string; label: string };
}) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const size = CELL * view.scale;
    const at = inspectPlacement({ left: view.x + cell.x * size, top: view.y + cell.y * size, size }, { width: el.offsetWidth, height: el.offsetHeight }, stage);
    el.style.left = `${at.left}px`;
    el.style.top = `${at.top}px`;
  });
  return (
    <div ref={ref} className={frame.className} role={frame.role} aria-label={frame.label} data-inspect-cell={`${cell.x},${cell.y}`}>
      {children}
    </div>
  );
}
